import React, { useState, useLayoutEffect, useCallback, Fragment } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Actions, FEATURE, Items } from 'containers/Landing/logConstants'

import { useMetricsLog } from 'helpers/uilogging/hooks'
import { EmptyList, Spinner, Tooltip } from 'react-wot-bootstrap'
import ButtonSimple from 'components/ButtonSimple/ButtonSimple'

import {
    fetchRecommendedClans,
    selectNextPage,
    selectPrevPage,
    setSearchTerm,
    formatCooldownError,
    fetchRecommendedClansWithSettings,
} from 'state/actions/recommended_clans'
import { openInvitations } from 'state/actions/app'
import { clanInvitesChecked } from 'state/actions/clan_invites'

import { ITEMS_PER_PAGE } from 'state/reducers/recommended_clans'
import {
    LANDING_CLANS_DISABLED_FILTER_TOOLTIP,
    LANDING_CLANS_HEADER,
    LANDING_CLANS_SUBTITLE,
    LANDING_CLANS_SUBTITLE_SEARCH,
    LANDING_CLANS_EMPTY_MESSAGE,
    LANDING_CLANS_EMPTY_SEARCH_MESSAGE,
    LANDING_CLANS_SPINNER,
    LANDING_CLANS_FILTER_SPINNER,
    LANDING_CLANS_ERROR_TITLE,
    LANDING_CLANS_ERROR_MESSAGE,
    LANDING_CLANS_ERROR_BUTTON,
} from './constants'
import { LANDING_GOTO_INVITATIONS_TOOLTIP, LANDING_GOTO_INVITATIONS, LANDING_GOTO_BACK } from '../../translations'
import Card from './components/Card/Card'
import SearchControl from './components/SearchControl/SearchControl'
import Pagination from './components/Pagination/Pagination'
import FiltersButton from './components/FiltersButton/FiltersButton'
import SearchResult from './components/SearchResult/SearchResult'
import Filters from './components/Filters/Filters'
import Back from 'components/Back/Back'
import styles from './LandingClans.scss'

export const LandingClans = ({ partnerID, onLastSlideBackClick }) => {
    const dispatch = useDispatch()
    const {
        loadedBefore,
        loading,
        items,
        hasError,
        paging,
        pagingSearch,
        searchTerm,
        total,
        dwhType,
        membersCount,
        listType,
        languages,
        joinResult,
        loadingSettings,
    } = useSelector((state) => state.recommendedClans)
    const { total: clanInvitesTotal } = useSelector((state) => state.clanInvites)
    const { cooldownTill } = joinResult
    const log = useMetricsLog(FEATURE)
    const isSearch = searchTerm !== null
    const activePaging = isSearch ? pagingSearch : paging

    const [isFiltersOpened, setIsFiltersOpened] = useState(false)
    const [isPageChanging, setIsPageChanging] = useState(false)
    const [visibleItems, setVisibleItems] = useState([])
    const [isFilterEnabled, setIsFilerEnabled] = useState(false)

    useLayoutEffect(() => {
        if (!loadedBefore && !loadingSettings) {
            dispatch(fetchRecommendedClansWithSettings())
        }
    }, [loadedBefore, loadingSettings])

    useLayoutEffect(() => {
        setVisibleItems([...items].splice(activePaging.current * ITEMS_PER_PAGE, ITEMS_PER_PAGE))

        setIsPageChanging(true)
        const timerId = setTimeout(() => {
            setIsPageChanging(false)
        }, 100)
        setIsFilerEnabled(false)

        return () => clearTimeout(timerId)
    }, [items, activePaging])

    const toggleFiltersPopup = useCallback(
        (isFiltersApplied) => {
            handleFiltersPopup()
            setIsFiltersOpened(!isFiltersOpened)

            setIsFilerEnabled(isFiltersApplied === true)
        },
        [isFiltersOpened]
    )

    const handleSetSearchTerm = useCallback((searchTerm) => {
        dispatch(setSearchTerm(searchTerm))
        dispatch(fetchRecommendedClans())
    }, [])

    const handleSelectNextPage = useCallback(() => {
        dispatch(selectNextPage())
        handlePaginationClick(Items.PaginationRightButton)
    }, [])

    const handleSelectPrevPage = useCallback(() => {
        dispatch(selectPrevPage())
        handlePaginationClick(Items.PaginationLeftButton)
    }, [])

    const handleButtonClick = useCallback(
        (item) => {
            log({
                action: Actions.Click,
                item,
                parentScreen: Items.Hangar,
                partnerID,
            })
        },
        [log]
    )

    const handleInvitationsClick = () => {
        handleButtonClick(Items.ClanInvitationsButton)
        dispatch(openInvitations())
        dispatch(clanInvitesChecked())
    }

    const handleFiltersPopup = useCallback(() => {
        log({
            action: isFiltersOpened ? Actions.Close : Actions.Open,
            item: Items.FiltersSettingsWindow,
            parentScreen: Items.LandingClans,
            partnerID,
        })
    }, [log])

    const handlePaginationClick = useCallback(
        (item) => {
            log({
                action: Actions.Click,
                item: item,
                parentScreen: Items.LandingClans,
                partnerID,
            })
        },
        [log]
    )

    if (hasError) {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.title}>{LANDING_CLANS_HEADER}</div>
                </div>
                <div className={styles.content}>
                    <EmptyList
                        title={LANDING_CLANS_ERROR_TITLE}
                        message={LANDING_CLANS_ERROR_MESSAGE}
                        buttonCaption={LANDING_CLANS_ERROR_BUTTON}
                        onButtonClick={() => dispatch(fetchRecommendedClans())}
                    />
                </div>
            </div>
        )
    }

    return (
        <div className={styles.base}>
            <div className={styles.header}>
                <div className={styles.title}>{LANDING_CLANS_HEADER}</div>
                <div className={styles.subtitle}>
                    {!isSearch && cooldownTill === null && LANDING_CLANS_SUBTITLE}
                    {!isSearch && cooldownTill !== null && formatCooldownError(cooldownTill, true)}
                    {isSearch && LANDING_CLANS_SUBTITLE_SEARCH}
                </div>
            </div>
            <div className={styles.filters}>
                <div>
                    <ButtonSimple
                        className={styles.button}
                        tooltipId="goto_invitations_tooltip_clans"
                        isContrast
                        onClick={handleInvitationsClick}
                        messages={clanInvitesTotal}
                    >
                        {LANDING_GOTO_INVITATIONS}
                    </ButtonSimple>
                    <Tooltip id="goto_invitations_tooltip_clans">{LANDING_GOTO_INVITATIONS_TOOLTIP}</Tooltip>
                </div>
                <div className={styles.filtersItem}>
                    {isSearch ? (
                        <SearchResult result={searchTerm} />
                    ) : (
                        <FiltersButton onClick={toggleFiltersPopup} />
                    )}
                </div>
                <div>
                    <SearchControl
                        setSearchTerm={handleSetSearchTerm}
                        partnerID={partnerID}
                        searchTerm={searchTerm}
                    />
                </div>
            </div>
            <div className={styles.content}>
                <div className={cx(styles.clans, isPageChanging && styles.clans__blured)}>
                    {loading && (
                        <Spinner caption={isFilterEnabled ? LANDING_CLANS_FILTER_SPINNER : LANDING_CLANS_SPINNER} />
                    )}
                    {!loading && (
                        <Fragment>
                            {visibleItems.map((item, index) => (
                                <Card
                                    key={item.id}
                                    clan={item}
                                    partnerID={partnerID}
                                    clanNumber={index}
                                    dwhType={dwhType}
                                    total={total}
                                    membersCount={membersCount}
                                    listType={listType}
                                    languages={languages}
                                />
                            ))}
                            {items.length === 0 && (
                                <div className={styles.message}>
                                    {isSearch ? LANDING_CLANS_EMPTY_SEARCH_MESSAGE : LANDING_CLANS_EMPTY_MESSAGE}
                                </div>
                            )}
                        </Fragment>
                    )}
                </div>
            </div>
            <div className={cx(styles.pagination, (loading || activePaging.total < 2) && styles.pagination__invisible)}>
                <Pagination
                    paging={activePaging}
                    selectPrevPage={handleSelectPrevPage}
                    selectNextPage={handleSelectNextPage}
                />
            </div>

            <Tooltip id="disabled-filters-tooltip">{LANDING_CLANS_DISABLED_FILTER_TOOLTIP}</Tooltip>
            <div className={styles.back}>
                <Back handleClick={onLastSlideBackClick}>
                    <span className={styles.backTitle}>{LANDING_GOTO_BACK}</span>
                </Back>
            </div>
            <Filters
                isFull={dwhType !== null}
                isOpened={isFiltersOpened}
                onClose={toggleFiltersPopup}
                partnerID={partnerID}
            />
        </div>
    )
}

LandingClans.propTypes = {
    partnerID: PropTypes.string.isRequired,
    onLastSlideBackClick: PropTypes.func.isRequired,
}

export default LandingClans



// WEBPACK FOOTER //
// ./containers/Landing/components/LandingClans/LandingClans.jsx